import { AirResponseDTO } from "../api/response.types";
import {
  AirAirlineModel,
  AirEnrichedEventModel,
  AirEventModel,
  AirFlightLegModel,
  AirGroupedEventsModel,
  AirLocationModel,
  AirRouteSegmentModel,
  AirShipmentModel,
  AirShipmentTimelineModel,
  AirTransformedEventModel,
} from "./types";
import { airErrorMessageMap, AirMessagesModel, AirStatusesModel } from "entities/shared/airCard.types";
import {
  CoordinateModel,
  DateTimeModel,
  SearchReferenceModel,
  ShipmentVariantModel,
} from "entities/shared/shipmentCard.types";
import { RouteDataType, TransportTypes } from "shared/api/lib/map.types";
import { MapRoute, MapRouteModel } from "entities/shared/mapRouteModel";
import { SubscriptionDataModel } from "entities/shared/subscriptionModel";
import { calcProgress } from "entities/shared/utils";
import { BaseModel } from "shared/api/lib/baseModel";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { isAppIntegration } from "shared/utils/url.utils";

import { AirSearchApiParser } from "../api/parser";

export const isAirSearchCardModel = (model: Nullable<{ responseType: string }>): model is AirSearchCardModel =>
  model?.responseType === ShipmentVariantModel.AirSearch;

export const isAirSearchShipmentModel = isAirSearchCardModel;

export class AirSearchCardModel extends BaseModel<AirSearchApiParser, AirResponseDTO> {
  public readonly responseType: ShipmentVariantModel;
  public readonly requestLimitReachedAppIntegration: boolean = false;
  public readonly requestLimitReachedWebApp: boolean = false;
  private _route: MapRoute | null = null;

  constructor(DTO: AirResponseDTO) {
    super({ apiParser: new AirSearchApiParser(DTO), DTO });
    this._route = new MapRoute(this.adaptRouteData());
    this.responseType = ShipmentVariantModel.AirSearch;
    this.requestLimitReachedAppIntegration = this.requestLimitReached && isAppIntegration;
    this.requestLimitReachedWebApp = this.requestLimitReached && !isAppIntegration;
  }

  get isSuccess(): boolean {
    return this.apiParser.isSuccess;
  }

  get hasData(): boolean {
    return this.apiParser.hasData;
  }

  get requestLimitReached(): boolean {
    return this.apiParser.requestLimitReached;
  }

  get message(): ValueOf<typeof AirMessagesModel> | string {
    return this.apiParser.message;
  }

  get reference(): SearchReferenceModel | null {
    return this.apiParser.reference ?? null;
  }

  get status(): ValueOf<typeof AirStatusesModel> {
    return this.apiParser.status;
  }

  get airline(): AirAirlineModel | null {
    return this.apiParser.airline;
  }

  get airlineName(): string {
    return this.apiParser.airlineName;
  }

  get carrierName(): AirAirlineModel | null {
    return this.apiParser.airline;
  }

  get fromDirection(): string | null {
    return this.apiParser.fromDirection;
  }

  get toDirection(): string | null {
    return this.apiParser.toDirection;
  }

  /** Aliases used by AirSearchCard UI */
  get directionFrom(): string | null {
    return this.fromDirection;
  }

  get directionTo(): string | null {
    return this.toDirection;
  }

  get shipment(): AirShipmentModel | null {
    return this.apiParser.shipment;
  }

  get pieces(): string | null {
    return this.apiParser.pieces;
  }

  get piece(): number {
    return this.apiParser.piece;
  }

  get weight(): number | null {
    return this.apiParser.weight;
  }

  get iataCode(): string {
    return this.apiParser.iataCode;
  }

  get fromCode(): string {
    return this.apiParser.fromCode;
  }

  get toCode(): string {
    return this.apiParser.toCode;
  }

  /** Demo/cache banner text — not provided by Qubictron air API. */
  get cacheComment(): string | null {
    return null;
  }

  public hasPin(): boolean {
    return this.apiParser.hasPin;
  }

  get polCoordinate(): CoordinateModel | null {
    return this.apiParser.polCoordinate;
  }

  get podCoordinate(): CoordinateModel | null {
    return this.apiParser.podCoordinate;
  }

  get routeData() {
    return this.apiParser.routeData;
  }

  get currentPin(): CoordinateModel | null {
    return this.apiParser.currentPin;
  }

  get progress(): number {
    return calcProgress({
      startDate: this.atd ?? this.etd,
      endDate: this.ata ?? this.eta,
      airStatus: this.status,
    });
  }

  get etd(): DateTimeModel | null {
    return this.apiParser.etd;
  }

  get atd(): DateTimeModel | null {
    return this.apiParser.atd;
  }

  get eta(): DateTimeModel | null {
    return this.apiParser.eta;
  }

  get ata(): DateTimeModel | null {
    return this.apiParser.ata;
  }

  get predictiveETA(): DateTimeModel | null {
    return null;
  }

  get searchError(): ValueOf<typeof AirMessagesModel> | string {
    return this.apiParser.searchError;
  }

  public searchErrorText(defaultText: string): string | null {
    const searchMessage = this.apiParser.searchError;
    if (searchMessage === AirMessagesModel.OK || searchMessage === "OK") return null;
    if (searchMessage in airErrorMessageMap) {
      return airErrorMessageMap[searchMessage as ValueOf<typeof AirMessagesModel>] || defaultText;
    }
    return defaultText;
  }

  get locations(): AirLocationModel[] {
    return this.apiParser.locations.map((location) => ({
      ...location,
      countryCode: location.country?.code ?? null,
    }));
  }

  get events(): AirEventModel[] {
    return this.apiParser.events;
  }

  get routeSegments(): AirRouteSegmentModel[] {
    return this.apiParser.routeSegments;
  }

  /** Flight legs for AirlineList tab (from route_segments). */
  get airlines(): AirFlightLegModel[] {
    return this.routeSegments.map((segment) => ({
      flightNumber: segment.transport?.number ?? null,
      origin: this.apiParser.getLocationName(this.apiParser.getLocationByCode(segment.departure?.location ?? null)),
      destination: this.apiParser.getLocationName(this.apiParser.getLocationByCode(segment.arrival?.location ?? null)),
      atd: segment.departure?.actual ?? null,
      etd: segment.departure?.estimated ?? null,
      ata: segment.arrival?.actual ?? null,
      eta: segment.arrival?.estimated ?? null,
      transportType: segment.transport?.mode ?? null,
    }));
  }

  getTransformedEvents(targetEvents?: Nullable<AirEventModel[]>): AirTransformedEventModel[] {
    return this.apiParser.getTransformedEvents(targetEvents);
  }

  get enrichedEvents(): AirEnrichedEventModel[] {
    return this.events.map((event) => {
      const locationData = this.locations.find((location) => location.id === event.location) ?? null;
      const displayDateTime = event.date_time.actual ?? event.date_time.estimated;
      return {
        ...event,
        locationData,
        locationLabel: this.apiParser.getLocationName(locationData),
        displayDateTime,
        isActual: !!event.date_time.actual,
      };
    });
  }

  get eventGroups(): AirGroupedEventsModel[] {
    const groups: AirGroupedEventsModel[] = [];
    const originCode = this.shipment?.departure?.location ?? null;
    const destinationCode = this.shipment?.arrival?.location ?? null;
    let previousLocationCode: string | null = null;

    this.enrichedEvents.forEach((event) => {
      const locationCode = event.location;
      const matchesPrevious = previousLocationCode !== null && previousLocationCode === locationCode;

      if (!matchesPrevious) {
        let locationRole: AirGroupedEventsModel["locationRole"] = "Transit";
        if (locationCode === originCode) locationRole = "Origin";
        if (locationCode === destinationCode) locationRole = "Destination";

        groups.push({
          location: event.locationData,
          locationLabel: event.locationLabel,
          locationRole,
          events: [event],
        });
      } else {
        groups[groups.length - 1].events.push(event);
      }

      previousLocationCode = locationCode;
    });

    return groups;
  }

  get shipmentTimeline(): AirShipmentTimelineModel | null {
    if (!this.reference) return null;

    const quantity = this.shipment?.cargo_details?.quantity;
    const weight = this.shipment?.cargo_details?.weight;
    const cargoParts: string[] = [];
    if (typeof quantity === "number") cargoParts.push(`${quantity} pcs`);
    if (typeof weight === "number") cargoParts.push(`${weight} kg`);

    const lastEvent = this.enrichedEvents[this.enrichedEvents.length - 1];

    return {
      awbNumber: this.reference,
      cargoLabel: cargoParts.join(" · ") || "Air cargo",
      status: this.status,
      lastUpdatedAt: lastEvent?.displayDateTime ?? null,
      events: this.eventGroups,
    };
  }

  get route(): MapRoute | null {
    return this._route;
  }

  private adaptRouteData(): MapRouteModel {
    const legs = this.routeData?.route_legs ?? [];

    return {
      route: legs
        .filter((leg) => leg.route_path?.length)
        .map((leg) => ({
          path: leg.route_path ?? [],
          type: RouteDataType.AIR,
          transportType: TransportTypes.AIR,
        })),
      locations: this.apiParser.mapLocations.map((location) => ({
        name: location.name,
        countryCode: location.countryCode,
        lat: location.coordinates?.lat,
        lng: location.coordinates?.lng,
      })),
      ...(this.currentPin && this.status !== AirStatusesModel.DELIVERED ? { currentPin: this.currentPin } : {}),
    };
  }

  get subscription(): SubscriptionDataModel | null {
    return null;
  }
}
